import React from 'react'
import thunkInject from 'redux-thunk-inject'
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly'
import { ConnectedRouter, routerMiddleware } from 'connected-react-router'
import { createBrowserHistory } from 'history'
import { createStore, applyMiddleware } from 'redux'
import { Provider } from 'react-redux'
import { hydrate } from 'react-dom'
import setPublicPath from 'app/setPublicPath'
import { App } from 'app/containers'
import appReducer from 'app/appReducer'
import { Sidebar } from 'app/components/index'
import { routerTrackEvent } from 'app/utils/riskified'
import cookiesMiddleware, { getStateFromCookies } from '@ynap/redux-cookies-middleware'
import { createMemoizedFetch } from '@ynap/api-utils'
import { raygun } from '@ynap/clientside-monitoring'
import { createFetchWcs } from '@ynap/fetch'
import { getHeadConfig, getLocale } from '@ynap/config-state'
import { StylingProvider, createRenderer } from '@ynap/fela'
import { AddressValidationProvider } from '@ynap/address-form-v1'
import { OptimizelyProvider, createInstance } from '@ynap/optimizely-utils'

export default (messages) => {
    const history = createBrowserHistory()
    const cookiePaths = { uberToken: { name: 'ubertoken' } }
    const initialState = getStateFromCookies(window.CheckoutState, cookiePaths)

    // we need to initialise fetch with a store BEFORE middleware, so have to pass a ref and update as soon as the store is created
    const storeRef = {}
    const fetchWcs = createFetchWcs(fetch, storeRef)
    const locale = getLocale(initialState)
    const isBare = !!initialState.deviceType

    const enhancer = applyMiddleware(routerMiddleware(history), thunkInject(createMemoizedFetch(fetchWcs)), cookiesMiddleware(cookiePaths))
    const composeEnhancers = composeWithDevTools({ trace: true })
    const store = createStore(appReducer(history), initialState, composeEnhancers(enhancer))

    const state = store.getState()
    const headConfig = getHeadConfig(state)
    const { brand, assetsBaseUrl } = headConfig

    const renderer = createRenderer({ brand })

    initialState.messages = messages

    storeRef.getState = store.getState
    storeRef.dispatch = store.dispatch

    if (headConfig.raygun && headConfig.raygun.enabled && headConfig.raygun.apiKey) {
        raygun.agent.init(headConfig.raygun)
        raygun.helpers.routerTrackEvent(history)
    }

    if (headConfig.riskified && headConfig.riskified.enabled) {
        routerTrackEvent(history)
    }

    const sdkKey = headConfig?.optimizely?.sdkKey
    const datafile = window.optimizelyCOState?.datafile
    const useSifuOptimizely = !headConfig?.optimizely?.mockOptimizely

    const optimizely =
        window.optimizelyCreateInstance && useSifuOptimizely
            ? window.optimizelyCreateInstance
            : (sdkKey || datafile) &&
              createInstance({
                  sdkKey,
                  datafile,
                  datafileOptions: {
                      autoUpdate: true,
                      updateInterval: 600000, // 10 minutes in milliseconds
                  },
                  disableLogger: false, // turn on logs for dev env
              })

    setPublicPath(`${assetsBaseUrl}/`)

    hydrate(
        <Provider store={store}>
            <AddressValidationProvider {...state.addressValidationConfig}>
                <ConnectedRouter history={history}>
                    <StylingProvider renderer={renderer} brand={brand}>
                        <App messages={messages} locale={locale} isBare={isBare} optimizely={optimizely} nativeapp={initialState.deviceType} />
                    </StylingProvider>
                </ConnectedRouter>
            </AddressValidationProvider>
        </Provider>,
        document.querySelector('.wrapper')
    )

    hydrate(
        <Provider store={store}>
            <StylingProvider renderer={renderer} brand={brand}>
                <OptimizelyProvider optimizely={optimizely} pageGroup="checkout" nativeapp={initialState.deviceType}>
                    <ConnectedRouter history={history}>
                        <Sidebar messages={messages} locale={locale} optimizely={optimizely} flags={headConfig?.flags} />
                    </ConnectedRouter>
                </OptimizelyProvider>
            </StylingProvider>
        </Provider>,
        document.getElementById('sidebar')
    )
}
